import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Building2, Receipt, Layers, Link2, CheckCircle2 } from "lucide-react";
import { formatMoney } from "@/lib/currency";

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" }); } catch { return s; }
}

const METHODS = ["UPI", "Cash", "Cheque", "Bank Transfer", "Card"];

export default function BulkRenew() {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState(null);
  const [search, setSearch] = useState("");
  const [activeOrg, setActiveOrg] = useState(null);   // { company_name, mobile_no, ... }
  const [bundle, setBundle] = useState(null);         // { items: [...] }
  const [selected, setSelected] = useState(new Set());
  const [planChoice, setPlanChoice] = useState({});   // { subscriber_id: plan_id }
  const [amounts, setAmounts] = useState({});         // { subscriber_id: number }
  const [method, setMethod] = useState("UPI");
  const [upiRef, setUpiRef] = useState("");
  const [chequeNo, setChequeNo] = useState("");
  const [chequeBank, setChequeBank] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);         // success summary

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/renewals/org-customers");
        setOrgs(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
        setOrgs([]);
      }
    })();
  }, []);

  const loadBundle = async (org) => {
    setActiveOrg(org); setBundle(null); setSelected(new Set()); setPlanChoice({}); setAmounts({}); setResult(null);
    try {
      const { data } = await api.get("/renewals/org-bundle", {
        params: { company_name: org.company_name, mobile_no: org.mobile_no },
      });
      setBundle(data);
      // pre-select all and default to current plan
      const next = new Set();
      const planMap = {}; const amtMap = {};
      data.items.forEach((it) => {
        next.add(it.subscriber_id);
        const def = (it.available_plans || []).find((p) => p.id === it.current_plan_id) || it.available_plans?.[0];
        if (def) { planMap[it.subscriber_id] = def.id; amtMap[it.subscriber_id] = Number(def.price) || 0; }
      });
      setSelected(next); setPlanChoice(planMap); setAmounts(amtMap);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const toggle = (sid) => {
    const n = new Set(selected); n.has(sid) ? n.delete(sid) : n.add(sid); setSelected(n);
  };
  const toggleAll = () => {
    if (!bundle) return;
    if (selected.size === bundle.items.length) setSelected(new Set());
    else setSelected(new Set(bundle.items.map((x) => x.subscriber_id)));
  };

  const total = useMemo(() => {
    let t = 0;
    selected.forEach((sid) => { t += Number(amounts[sid] || 0); });
    return t;
  }, [selected, amounts]);

  const onPlanChange = (sid, planId) => {
    setPlanChoice((m) => ({ ...m, [sid]: planId }));
    const item = bundle?.items.find((x) => x.subscriber_id === sid);
    const plan = item?.available_plans?.find((p) => p.id === planId);
    if (plan) setAmounts((m) => ({ ...m, [sid]: Number(plan.price) || 0 }));
  };

  const filteredOrgs = useMemo(() => {
    if (!orgs) return null;
    if (!search.trim()) return orgs;
    const s = search.toLowerCase();
    return orgs.filter((o) => (o.company_name || "").toLowerCase().includes(s) || (o.mobile_no || "").includes(s) || (o.gst_number || "").toLowerCase().includes(s));
  }, [orgs, search]);

  const submit = async () => {
    if (selected.size === 0) { toast.error("Select at least one subscription"); return; }
    if (method === "Cheque" && (!chequeNo || !chequeBank)) { toast.error("Cheque number and bank required"); return; }
    const items = Array.from(selected).map((sid) => ({
      subscriber_id: sid,
      new_plan_id: planChoice[sid],
      payment_amount: Number(amounts[sid] || 0),
    }));
    const missingPlan = items.find((x) => !x.new_plan_id);
    if (missingPlan) { toast.error("Pick a renewal plan for every selected row"); return; }

    setSubmitting(true);
    try {
      const body = {
        items,
        payment_method: method,
        upi_ref: upiRef || null,
        cheque_number: chequeNo || null,
        cheque_bank: chequeBank || null,
        backfill: false,
        renewal_type: "manual_renewal",
      };
      const { data } = await api.post("/renewals/bulk-renew", body);
      setResult(data);
      toast.success(`Renewed ${data.succeeded} subscription${data.succeeded !== 1 ? "s" : ""} · ₹${data.total_amount.toLocaleString("en-IN")}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  const downloadInvoice = async (paymentId) => {
    try {
      const token = localStorage.getItem("access_token");
      const res = await fetch(`${API_BASE}/payments/${paymentId}/invoice.pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { toast.error("PDF unavailable"); return; }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `invoice-${paymentId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(e.message); }
  };

  // ----- result screen ---------------------------------------------------
  if (result) {
    return (
      <div data-testid="bulk-renew-result" className="mx-auto max-w-4xl">
        <Button variant="ghost" size="sm" asChild className="mb-3 rounded-sm text-slate-600">
          <Link to="/renewals"><ArrowLeft size={14} className="mr-1" />Back to Renewals</Link>
        </Button>
        <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-5">
          <div className="flex items-center gap-2 text-emerald-800">
            <CheckCircle2 size={20} />
            <h1 className="text-2xl font-semibold tracking-tight">Bulk renewal complete</h1>
          </div>
          <p className="mt-1 text-sm text-emerald-900">
            <span className="font-mono font-semibold">{result.succeeded}</span> succeeded · <span className="font-mono font-semibold">{result.failed}</span> failed · Total <span className="font-mono font-semibold">{formatMoney(result.total_amount, "INR")}</span>
          </p>
          <p className="mt-1 text-xs text-emerald-800">Bulk ID: <span className="font-mono">{result.bulk_renewal_id}</span> — every child payment + term is tagged with this ID.</p>
        </div>

        <div className="mt-4 rounded-sm border border-slate-200 bg-white">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Subscriber</TableHead>
                <TableHead>New term</TableHead>
                <TableHead>Invoice</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {result.results.map((r) => (
                <TableRow key={r.subscriber_id} data-testid={`result-row-${r.subscriber_id}`}>
                  <TableCell className="font-mono text-xs">{r.subscriber_id.slice(0, 8)}…</TableCell>
                  <TableCell className="text-sm">{fmtDate(r.new_start_date)} → {fmtDate(r.new_end_date)}</TableCell>
                  <TableCell className="font-mono text-xs">{r.invoice?.invoice_number || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => downloadInvoice(r.payment_id)} className="rounded-sm" data-testid={`download-invoice-${r.subscriber_id}`}>
                      <Receipt size={12} className="mr-1" />PDF
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {(result.errors || []).map((e, i) => (
                <TableRow key={i} className="bg-rose-50/40">
                  <TableCell className="font-mono text-xs">{e.subscriber_id.slice(0, 8)}…</TableCell>
                  <TableCell colSpan={3} className="text-xs text-rose-700">FAILED — {typeof e.error === "string" ? e.error : JSON.stringify(e.error)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="mt-4 flex gap-2">
          <Button variant="outline" onClick={() => { setResult(null); setActiveOrg(null); setBundle(null); }} className="rounded-sm" data-testid="bulk-renew-another">
            Renew another organization
          </Button>
          <Button onClick={() => navigate("/renewals")} className="rounded-sm bg-[#002FA7] hover:bg-[#00227A]">
            Done
          </Button>
        </div>
      </div>
    );
  }

  // ----- step 1: pick org ------------------------------------------------
  if (!activeOrg) {
    return (
      <div data-testid="bulk-renew-page" className="mx-auto max-w-5xl">
        <Button variant="ghost" size="sm" asChild className="mb-3 rounded-sm text-slate-600">
          <Link to="/renewals"><ArrowLeft size={14} className="mr-1" />Back to Renewals</Link>
        </Button>
        <div className="flex items-center gap-3">
          <div className="rounded-sm bg-[#002FA7] p-2 text-white"><Building2 size={20} /></div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Org Renewal · Bulk</div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Pick an organization</h1>
            <p className="mt-1 text-sm text-slate-500">Org customers with 2+ active periodicals are best candidates — renew them all in one bank reference.</p>
          </div>
        </div>

        <div className="mt-6 rounded-sm border border-slate-200 bg-white">
          <div className="border-b border-slate-100 p-3">
            <Input
              data-testid="bulk-renew-search"
              placeholder="Search by company name, mobile, or GSTIN…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-sm"
            />
          </div>
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Organization</TableHead>
                <TableHead>GSTIN</TableHead>
                <TableHead>City</TableHead>
                <TableHead className="text-center">Periodicals</TableHead>
                <TableHead>Earliest expiry</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orgs === null && [...Array(4)].map((_, i) => (
                <TableRow key={i}><TableCell colSpan={6}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
              ))}
              {filteredOrgs && filteredOrgs.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-12 text-center text-sm text-slate-500">No organization customers found.</TableCell></TableRow>
              )}
              {(filteredOrgs || []).map((o) => (
                <TableRow key={`${o.company_name}::${o.mobile_no}`} data-testid={`org-row-${o.company_name.replace(/\W+/g, "-")}`}>
                  <TableCell>
                    <div className="font-medium text-slate-900">{o.company_name}</div>
                    <div className="font-mono text-[11px] text-slate-500">{o.mobile_no}</div>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-700">{o.gst_number || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-700">{o.city || "—"}{o.state ? `, ${o.state}` : ""}</TableCell>
                  <TableCell className="text-center">
                    <span className="rounded-sm border border-indigo-200 bg-indigo-50 px-2 py-0.5 font-mono text-xs font-semibold text-[#002FA7]">{o.periodicals_count}</span>
                  </TableCell>
                  <TableCell className="text-sm">{fmtDate(o.earliest_end)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" onClick={() => loadBundle(o)} className="rounded-sm bg-[#002FA7] hover:bg-[#00227A]" data-testid={`pick-org-${o.company_name.replace(/\W+/g, "-")}`}>
                      <Layers size={12} className="mr-1" />Renew bundle
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  // ----- step 2: bundle picker ------------------------------------------
  return (
    <div data-testid="bulk-renew-bundle" className="mx-auto max-w-6xl">
      <Button variant="ghost" size="sm" onClick={() => { setActiveOrg(null); setBundle(null); }} className="mb-3 rounded-sm text-slate-600">
        <ArrowLeft size={14} className="mr-1" />Pick a different organization
      </Button>

      <div className="rounded-sm border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-3">
          <div className="rounded-sm bg-indigo-100 p-2 text-[#002FA7]"><Building2 size={20} /></div>
          <div className="flex-1">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Bulk renewal</div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{activeOrg.company_name}</h1>
            <div className="mt-0.5 font-mono text-xs text-slate-500">
              {activeOrg.mobile_no}{activeOrg.gst_number ? ` · GSTIN ${activeOrg.gst_number}` : ""}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-sm border border-slate-200 bg-white">
        <Table className="data-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={!!bundle && selected.size === bundle.items.length && bundle.items.length > 0}
                  onCheckedChange={toggleAll}
                  data-testid="bulk-toggle-all"
                />
              </TableHead>
              <TableHead>Pub House → Periodical</TableHead>
              <TableHead>Current end</TableHead>
              <TableHead>Renewal Plan</TableHead>
              <TableHead className="text-right">Amount (₹)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!bundle && [...Array(3)].map((_, i) => <TableRow key={i}><TableCell colSpan={5}><Skeleton className="h-6 w-full" /></TableCell></TableRow>)}
            {bundle?.items.map((it) => {
              const checked = selected.has(it.subscriber_id);
              return (
                <TableRow key={it.subscriber_id} data-testid={`bundle-row-${it.subscriber_id}`}>
                  <TableCell>
                    <Checkbox checked={checked} onCheckedChange={() => toggle(it.subscriber_id)} data-testid={`row-check-${it.subscriber_id}`} />
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-medium text-slate-900">{it.publication_house_name}</div>
                    <div className="text-xs text-slate-600">→ {it.periodical_title}</div>
                    <div className="font-mono text-[10px] text-slate-400">{it.subscriber_code}</div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {fmtDate(it.subscription_end_date)}
                    {it.days_to_expiry !== null && (
                      <div className={`mt-0.5 text-[10px] ${it.days_to_expiry < 0 ? "text-rose-600" : it.days_to_expiry <= 30 ? "text-amber-600" : "text-emerald-700"}`}>
                        {it.days_to_expiry < 0 ? `${Math.abs(it.days_to_expiry)}d overdue` : `${it.days_to_expiry}d left`}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Select value={planChoice[it.subscriber_id] || ""} onValueChange={(v) => onPlanChange(it.subscriber_id, v)} disabled={!checked}>
                      <SelectTrigger className="rounded-sm" data-testid={`plan-select-${it.subscriber_id}`}><SelectValue placeholder="Pick plan" /></SelectTrigger>
                      <SelectContent>
                        {(it.available_plans || []).map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.plan_name} · {p.total_months}m · ₹{p.price}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    <Input
                      type="number"
                      step="0.01"
                      value={amounts[it.subscriber_id] ?? ""}
                      onChange={(e) => setAmounts((m) => ({ ...m, [it.subscriber_id]: e.target.value }))}
                      disabled={!checked}
                      className="ml-auto w-28 rounded-sm text-right font-mono"
                      data-testid={`amount-${it.subscriber_id}`}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Payment block */}
      <div className="mt-4 grid grid-cols-1 gap-3 rounded-sm border border-slate-200 bg-white p-5 sm:grid-cols-4">
        <div>
          <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Payment Method</Label>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger className="rounded-sm" data-testid="bulk-method"><SelectValue /></SelectTrigger>
            <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {(method === "UPI" || method === "Bank Transfer") && (
          <div className="sm:col-span-3">
            <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">UPI / Bank Reference</Label>
            <Input value={upiRef} onChange={(e) => setUpiRef(e.target.value)} placeholder="UTR / UPI reference" className="rounded-sm font-mono" data-testid="bulk-upi-ref" />
          </div>
        )}
        {method === "Cheque" && (
          <>
            <div>
              <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Cheque No</Label>
              <Input value={chequeNo} onChange={(e) => setChequeNo(e.target.value)} className="rounded-sm font-mono" data-testid="bulk-cheque-no" />
            </div>
            <div className="sm:col-span-2">
              <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Bank</Label>
              <Input value={chequeBank} onChange={(e) => setChequeBank(e.target.value)} className="rounded-sm" data-testid="bulk-cheque-bank" />
            </div>
          </>
        )}
      </div>

      <div className="mt-4 sticky bottom-0 flex flex-wrap items-center gap-4 rounded-sm border border-slate-200 bg-white p-4 shadow-lg">
        <div className="text-sm">
          <span className="font-semibold text-slate-900" data-testid="bulk-selected-count">{selected.size}</span> selected ·
          Bulk total: <span className="font-mono text-lg font-semibold text-[#002FA7]" data-testid="bulk-total">{formatMoney(total, "INR")}</span>
        </div>
        <div className="ml-auto flex items-center gap-2 text-xs text-slate-500">
          <Link2 size={12} /> One <span className="font-mono">bulk_renewal_id</span> · one invoice per periodical
        </div>
        <Button onClick={submit} disabled={submitting || selected.size === 0} data-testid="bulk-submit" className="rounded-sm bg-[#002FA7] hover:bg-[#00227A]">
          {submitting ? "Processing…" : `Renew ${selected.size} subscription${selected.size !== 1 ? "s" : ""}`}
        </Button>
      </div>
    </div>
  );
}
